import {
	ListOverridesQueryService,
	type SavedSearchModel,
} from '@supply-mover-2-0/core';
import type { ListOverrideAudience } from '@supply-mover-2-0/shared';
import clsx from 'clsx';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Select } from '../../../components/forms/Select';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { Toast } from '../../../services/ToastService';
import type { ListConfiguration } from '../../types';
import { SavedSearchManageTabs } from '../saved-search/SavedSearchManageTabs';

interface TabStripEditorProps {
	listKey: string;
	/** Bundled lists (resolved) — the tab pool, active or not. */
	editorLists: Array<ListConfiguration>;
	/** Every saved list the picker may pin into a strip. */
	savedSearches: Array<SavedSearchModel>;
	roles: Array<{ id: number; name: string }>;
	onSaved?: () => void;
}

/**
 * The audience tab strip (PD-14278): which tabs an entity's page shows and in
 * what order, for everyone or for one role. Replaces Settings > Tab
 * Configurations. The two-column drag editor is the same one people use for
 * their own tabs; what differs is who the result is for.
 *
 * The strip shown for a role is the everyone strip with that role's changes
 * on top, so editing it edits only the role's delta. Saving hands the server
 * the strip as shown; the server diffs it against the layer beneath.
 */
export function TabStripEditor({
	listKey,
	editorLists,
	savedSearches,
	roles,
	onSaved,
}: TabStripEditorProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const [audience, setAudience] =
		useState<ListOverrideAudience>('organization');
	const [roleId, setRoleId] = useState<number | null>(null);
	const [tabIds, setTabIds] = useState<Array<string>>([]);
	const [exists, setExists] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	// Bumps to remount the drag editor after a load or save so it re-seeds.
	const [generation, setGeneration] = useState(0);

	const load = useCallback(async () => {
		setIsLoading(true);
		const result = await ListOverridesQueryService.tabStripEditorView(
			listKey,
			audience,
			roleId,
		);
		if (result.success && result.data != null) {
			setTabIds(result.data.tabIds);
			setExists(result.data.exists);
			setGeneration((n) => n + 1);
		} else {
			Toast.error(t('Could not load the tab strip'));
		}
		setIsLoading(false);
	}, [listKey, audience, roleId, t]);

	useEffect(() => {
		void load();
	}, [load]);

	const handleSave = useCallback(
		async (nextTabIds: Array<string>) => {
			setIsSaving(true);
			try {
				const result = await ListOverridesQueryService.saveTabStrip({
					listKey,
					audience,
					roleId,
					tabIds: nextTabIds,
				});
				if (result.success) {
					Toast.success(
						audience === 'role'
							? t('Tab strip saved for the role')
							: t('Tab strip saved for everyone'),
					);
					onSaved?.();
					await load();
				} else {
					Toast.error(result.error ?? t('Could not save the tab strip'));
				}
			} finally {
				setIsSaving(false);
			}
		},
		[listKey, audience, roleId, t, onSaved, load],
	);

	// The pool the drag editor draws from: bundled lists as "default" entries
	// plus every non-private saved list. `SavedSearchManageTabs` derives tab
	// ids from these the same way the personal Manage Tabs panel does.
	const pool: Array<SavedSearchModel> = [
		...editorLists.map(
			(list): SavedSearchModel =>
				({
					id: list.id,
					name: list.name ?? list.id,
					description: null,
					listKey,
					baseListId: null,
					filterState: {},
					sortId: null,
					sortFields: null,
					columns: null,
					activeFilters: null,
					filterRules: null,
					listOverrides: null,
					visibility: 'organization',
					managerUserId: null,
					manager: null,
					allowEdit: false,
					isManager: false,
					canEdit: false,
					owner: null,
					sharedRoles: [],
					type: 'default',
					totalUseCount: 0,
					createdDate: null,
					modifiedDate: null,
				}) as SavedSearchModel,
		),
		...savedSearches.filter(
			(search) => search.type !== 'default' && search.visibility !== 'private',
		),
	];

	const audienceValue =
		audience === 'role' ? `role:${String(roleId ?? '')}` : 'organization';

	return (
		<div className="flex-1 flex flex-col overflow-hidden">
			<div
				className={clsx(
					'px-4 py-3 border-b flex items-center gap-3 flex-wrap',
					colors.classes.borderDefault,
					colors.classes.bgSecondary,
				)}
			>
				<span
					className={clsx('text-sm font-semibold', colors.classes.textPrimary)}
				>
					{t('Tab strip')}
				</span>
				<span className={clsx('text-xs', colors.classes.textSecondary)}>
					{t('Which tabs appear across the top, and in what order.')}
				</span>
				<div className="ml-auto flex items-center gap-2">
					<span
						className={clsx(
							'text-xs font-medium',
							colors.classes.textSecondary,
						)}
					>
						{t('For')}
					</span>
					<div className="w-52">
						<Select
							size="xs"
							options={[
								{ value: 'organization', label: t('Everyone') },
								...roles.map((role) => ({
									value: `role:${String(role.id)}`,
									label: t(role.name),
								})),
							]}
							value={audienceValue}
							disabled={isSaving}
							onChange={(value) => {
								if (value.startsWith('role:')) {
									const parsed = Number.parseInt(value.slice(5), 10);
									setAudience('role');
									setRoleId(Number.isNaN(parsed) ? null : parsed);
								} else {
									setAudience('organization');
									setRoleId(null);
								}
							}}
						/>
					</div>
					{exists && (
						<span className={clsx('text-xs', colors.classes.textMuted)}>
							{t('customized')}
						</span>
					)}
				</div>
			</div>
			<div className="flex-1 overflow-y-auto p-4">
				{isLoading ? (
					<span className={clsx('text-sm', colors.classes.textMuted)}>
						{t('Loading...')}
					</span>
				) : (
					<SavedSearchManageTabs
						key={`${audienceValue}:${String(generation)}`}
						searches={pool}
						currentTabIds={tabIds}
						onSave={(next) => {
							void handleSave(next);
						}}
						onCancel={() => {
							void load();
						}}
						isSaving={isSaving}
						tabsLabel={
							audience === 'role'
								? t(
										`Tabs for ${roles.find((role) => role.id === roleId)?.name ?? 'the role'}`,
									)
								: t('Tabs for everyone')
						}
					/>
				)}
			</div>
		</div>
	);
}
